import React, { useState, useEffect, useContext } from 'react';
import './SkillOrderControl.css';
import SocketContext from '../../../../../../../NewWebSocket/components/WebSocketAdapter';
import DataService, { SpellImage } from '../../../../../../../XXX/DataService';

interface SkillOrderControlProps {
  championId: string;
  championName: string;
  currentLevel: number;
  defaultSkillOrder: string[];
  currentSkillOrder?: string[]; // Add this prop to allow passing the current skill order
  onSkillOrderUpdate?: (skillOrder: string[]) => void;
}

const SkillOrderControl: React.FC<SkillOrderControlProps> = ({
  championId,
  championName,
  currentLevel,
  defaultSkillOrder,
  currentSkillOrder = [], // Default to empty array if not provided
  onSkillOrderUpdate
}) => {
  const socket = useContext(SocketContext);
  const [customMode, setCustomMode] = useState(false);
  // Use currentSkillOrder if provided, otherwise fall back to defaultSkillOrder
  const initialSkillOrder = currentSkillOrder.length > 0 ? [...currentSkillOrder] : [...defaultSkillOrder];
  const [skillOrder, setSkillOrder] = useState<string[]>(initialSkillOrder);
  const [remainingPoints, setRemainingPoints] = useState(0);
  
  // Add state for spell images
  const [spellImages, setSpellImages] = useState<SpellImage[]>([]);
  const [spellImagesLoading, setSpellImagesLoading] = useState(true);
  
  // Fetch spell images when the component mounts or championName changes
  useEffect(() => {
    const fetchSpellImages = async () => {
      try {
        setSpellImagesLoading(true);
        console.log(`Fetching spell images for champion: ${championName}`);
        const championData = await DataService.fetchChampionHeaderData(championName);
        console.log('Champion data received:', championData);
        
        if (championData && championData.spells && championData.spells.length > 0) {
          setSpellImages(championData.spells);
          console.log('Spell images set:', championData.spells);
        } else {
          console.warn('No spell images found in champion data:', championData);
          setSpellImages([]);
        }
      } catch (err) {
        console.error('Error fetching spell images:', err);
        setSpellImages([]);
      } finally {
        setSpellImagesLoading(false);
      }
    };
    
    fetchSpellImages();
  }, [championName]);
  
  // Count occurrences of each ability in the skill order
  const getSkillCounts = (order: string[]) => {
    const counts = { q: 0, w: 0, e: 0, r: 0 };
    order.forEach(skill => {
      const key = skill.toLowerCase();
      if (key in counts) {
        counts[key as keyof typeof counts]++;
      }
    });
    return counts;
  };
  
  // Calculate skill counts based on the current skill order
  const skillCounts = getSkillCounts(
    customMode 
      ? skillOrder.filter(skill => skill !== '').slice(0, currentLevel)
      : skillOrder.slice(0, currentLevel)
  );
  
  // Update skill order when props change
  useEffect(() => {
    if (!customMode) {
      // In default mode, display the default skill order
      setSkillOrder([...defaultSkillOrder]);
    }
  }, [defaultSkillOrder, currentSkillOrder, customMode]);
  
  // Calculate remaining points whenever skill order or level changes
  useEffect(() => {
    if (customMode) {
      const filledSlots = skillOrder.filter(skill => skill !== '').length;
      setRemainingPoints(currentLevel - filledSlots);
    } else {
      setRemainingPoints(0);
    }
  }, [currentLevel, skillOrder, customMode]);
  
  const canLevelBasicAbility = (abilityKey: string, levelIndex: number) => {
    const key = abilityKey.toLowerCase();
    const level = levelIndex + 1; // Convert from 0-based index to 1-based level
    
    // For level 1, we need to handle the case of replacing a skill
    // This level allows exactly one point of any basic ability
    if (level === 1) {
      // If we're clicking on the current skill at level 1, it's always allowed (to deselect it)
      if (skillOrder[0] === abilityKey) {
        return true;
      }
      
      // If this cell is empty OR we're replacing a different skill, it's valid
      if (skillOrder[0] === '' || skillOrder[0] !== '') {
        return true;
      }
    }
    
    // Check max points for basic abilities (Q/W/E) - max 5 points
    // Account for replacing an existing skill
    let adjustedCount = skillCounts[key as keyof typeof skillCounts];
    
    // If we're replacing a different skill at this level, don't count the new skill placement
    // toward the total count for that ability
    const currentSkillAtLevel = skillOrder[levelIndex];
    if (currentSkillAtLevel && currentSkillAtLevel.toLowerCase() === key) {
      adjustedCount--;
    }
    
    if (adjustedCount >= 5) {
      return false;
    }
    
    // Basic ability (Q/W/E) can't exceed ceil(level/2)
    // This means level 1 => 1, level 2 => 1, level 3 => 2, level 4 => 2, level 5 => 3, etc.
    const maxPoints = Math.ceil(level / 2);
    
    // Check if we already have too many points in this ability at this level
    if (adjustedCount >= maxPoints) {
      return false;
    }
    
    // Calculate how many points we already have in this ability
    const basicAbilityCount = adjustedCount;
    
    // First point in an ability is always OK if we have points available
    if (basicAbilityCount === 0) {
      return true;
    }
    
    // For subsequent points, check level requirements
    // Basic abilities can have a second point at level 3, third at level 5, etc.
    const requiredLevelForNextRank = basicAbilityCount * 2 - 1;
    return level >= requiredLevelForNextRank;
  };
  
  const canLevelUltimate = (levelIndex: number) => {
    const level = levelIndex + 1; // Convert 0-based index to 1-based level
    
    // Champion must be at least level 6 to learn ultimate
    if (currentLevel < 6) {
      return false;
    }
    
    // Ultimate can only be placed at level 6 or higher in the skill order
    if (level < 6) {
      return false;
    }
    
    // Max 3 points in ult
    if (skillCounts.r >= 3) {
      return false;
    }
    
    // Check champion level requirements for additional points
    if (skillCounts.r >= 1 && currentLevel < 11) {
      return false; // Need level 11+ for 2nd point
    }
    
    if (skillCounts.r >= 2 && currentLevel < 16) {
      return false; // Need level 16+ for 3rd point
    }
    
    return true;
  };
  
  const toggleCustomMode = () => {
    if (customMode) {
      // Switch back to default mode
      setSkillOrder([...defaultSkillOrder]);
      setCustomMode(false);
    } else {
      // Switch to custom mode - use the current skill order as starting point
      // If currentSkillOrder exists and is not empty, use it; otherwise use defaultSkillOrder
      const startingOrder = currentSkillOrder.length > 0 ? [...currentSkillOrder] : [...defaultSkillOrder];
      
      // Convert to a format with empty slots for custom editing
      const customOrder = Array(18).fill('');
      
      // Fill in with the starting order
      startingOrder.forEach((skill, index) => {
        if (index < customOrder.length) {
          customOrder[index] = skill;
        }
      });
      
      setSkillOrder(customOrder);
      setCustomMode(true);
    }
  };
  
  const resetCustomSkillOrder = () => {
    if (!customMode) return;
    
    // Reset to empty order in custom mode
    const emptyOrder = Array(18).fill('');
    setSkillOrder(emptyOrder);
    setRemainingPoints(currentLevel);
  };
  
  const handleCellClick = (spellKey: string, levelIndex: number) => {
    if (!customMode || levelIndex >= currentLevel) return;
    
    // Create a copy of the skill order
    const newOrder = [...skillOrder];
    
    // If this cell already has this spell, clear it
    if (newOrder[levelIndex] === spellKey) {
      newOrder[levelIndex] = '';
      setSkillOrder(newOrder);
      return;
    } 
    
    // If this cell already has a different spell, don't allow reassignment
    if (newOrder[levelIndex] !== '') {
      return;
    }
    
    // Only allow selecting skills in order
    // Check if all previous levels have skills assigned
    for (let i = 0; i < levelIndex; i++) {
      if (newOrder[i] === '') {
        // Found an empty slot before this level, don't allow selecting this one
        return;
      }
    }
    
    // Otherwise, add the spell if rules allow
    // Check if we can level this ability at this level
    const canLevel = spellKey.toLowerCase() === 'r' 
      ? canLevelUltimate(levelIndex) 
      : canLevelBasicAbility(spellKey, levelIndex);
    
    if (canLevel) {
      // Add the spell to this level
      newOrder[levelIndex] = spellKey;
      setSkillOrder(newOrder);
    }
  };

  // Handle clicking on the ability buttons (Q/W/E/R) to add to next available slot
  const handleAbilityButtonClick = (ability: string) => {
    if (!customMode || remainingPoints <= 0) return;
    
    const key = ability.toLowerCase();
    
    // Check if we've reached the maximum allowed points (5 for Q/W/E, 3 for R)
    const maxPoints = key === 'r' ? 3 : 5;
    if (skillCounts[key as keyof typeof skillCounts] >= maxPoints) {
      return; // Already at max points for this ability
    }
    
    // Look for the next empty slot where this ability can be placed
    const newOrder = [...skillOrder];
    
    for (let i = 0; i < currentLevel; i++) {
      if (newOrder[i] === '') {
        // Check if this ability can be placed at this level
        const canLevel = key === 'r' 
          ? canLevelUltimate(i) 
          : canLevelBasicAbility(ability, i);
        
        if (canLevel) {
          newOrder[i] = ability;
          setSkillOrder(newOrder);
          return;
        }
      }
    }
  };
  
  const applySkillOrder = () => {
    // Determine the final skill order to send
    const finalOrder = customMode 
      ? skillOrder.filter(skill => skill !== '') // In custom mode, filter out empty cells
      : [...defaultSkillOrder]; // In default mode, use default order
    
    console.log(`[SkillOrderControl] Applying skill order for ${championId}: ${finalOrder.join(',')}`);
    
    // Use onSkillOrderUpdate if available (static mode), otherwise use socket (WebSocket mode)
    if (onSkillOrderUpdate) {
      onSkillOrderUpdate(finalOrder);
    } else if (socket) {
      socket.emit('update-champion-skill-order', {
        championId,
        skillOrder: finalOrder
      });
    }
  };
  
  const isAbilityAvailableAtLevel = (ability: string, level: number) => {
    // Ultimate is only available for champions at least level 6
    // AND only at level 6+ in the skill order
    if (ability.toLowerCase() === 'r') {
      return currentLevel >= 6 && level >= 6;
    }
    
    return true; // Basic abilities are always available
  };
  
  const getAbilityMaxPoints = (ability: string) => {
    return ability.toLowerCase() === 'r' ? 3 : 5;
  };
  
  // Helper function to render a spell image alongside text
  const renderSpellImage = (index: number, keyLabel: string) => {
    if (spellImagesLoading || !spellImages || spellImages.length <= index) {
      return keyLabel;
    }
    
    const spell = spellImages[index];
    return (
      <div className="skill-order-with-image">
        <img 
          src={spell.imagePath} 
          alt={`${championName} ${keyLabel}`} 
          className="skill-order-spell-image" 
        />
        <span>{keyLabel}</span>
      </div>
    );
  };
  
  const renderSkillGrid = () => {
    const abilities = ['Q', 'W', 'E', 'R'];
    const levels = Array.from({ length: 18 }, (_, i) => i + 1);
    
    // Determine which skill order to display based on mode
    const displayOrder = customMode ? skillOrder : defaultSkillOrder;
    
    return (
      <div className="skill-order-grid-container">
        {/* Fixed left column with ability icons */}
        <div className="skill-order-grid-fixed">
          <div className="skill-order-header-fixed">
            <div className="skill-order-cell skill-order-header-cell">Skill</div>
          </div>
          {abilities.map((ability, index) => (
            <div key={ability} className="skill-order-row-fixed">
              <div className="skill-order-cell skill-order-ability-cell">
                {renderSpellImage(index, ability)}
              </div>
            </div>
          ))}
        </div>
        
        {/* Scrollable content */}
        <div className="skill-order-grid-scrollable">
          <div className="skill-order-grid">
            <div className="skill-order-header">
              {levels.map(level => (
                <div key={`header-${level}`} className={`skill-order-cell skill-order-header-cell ${level > currentLevel ? 'unavailable' : ''}`}>
                  {level}
                </div>
              ))}
            </div>
            
            {abilities.map((ability, index) => (
              <div key={ability} className="skill-order-row">
                {levels.map(level => {
                  const levelIndex = level - 1;
                  const isSelected = displayOrder[levelIndex] === ability;
                  const isUnavailable = level > currentLevel;
                  const isAbilityUnavailable = !isAbilityAvailableAtLevel(ability, level);
                  
                  // Check if this cell already has a different skill assigned
                  const hasDifferentSkill = displayOrder[levelIndex] && displayOrder[levelIndex] !== ability && displayOrder[levelIndex] !== '';
                  
                  // Check if there are empty slots before this level (out of sequence)
                  let isOutOfSequence = false;
                  if (customMode) {
                    for (let i = 0; i < levelIndex; i++) {
                      if (displayOrder[i] === '') {
                        isOutOfSequence = true;
                        break;
                      }
                    }
                  }
                  
                  // Determine if this cell is valid for this ability at this level
                  const isValid = ability.toLowerCase() === 'r' 
                    ? canLevelUltimate(levelIndex)
                    : canLevelBasicAbility(ability, levelIndex);
                  
                  // Show the cell as unavailable if:
                  // - it's beyond the current level
                  // - the ability can't be leveled here
                  // - a different skill is already assigned
                  // - or there are empty slots before this one (out of sequence)
                  const showAsUnavailable = isUnavailable || 
                                           (customMode && !isSelected && 
                                            (isAbilityUnavailable || !isValid || 
                                             hasDifferentSkill || isOutOfSequence));
                  
                  return (
                    <div 
                      key={`${ability}-${level}`}
                      className={`skill-order-cell skill-order-level-cell ${isSelected ? 'selected' : ''} ${showAsUnavailable ? 'unavailable' : ''} ${hasDifferentSkill ? 'has-different-skill' : ''}`}
                      onClick={() => !isUnavailable && customMode && handleCellClick(ability, levelIndex)}
                      title={getTooltipText(ability, level, isValid, isAbilityUnavailable)}
                    >
                      {isSelected && level}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };
  
  const getTooltipText = (ability: string, level: number, isValid: boolean, isAbilityUnavailable: boolean) => {
    const currentSkillAtLevel = skillOrder[level - 1];
    const levelIndex = level - 1;

    // If there's already a different skill at this level
    if (currentSkillAtLevel && currentSkillAtLevel !== ability && currentSkillAtLevel !== '') {
      return `Cannot reassign skills - clear the point first`;
    }
    
    // Check if all previous levels have skills assigned
    for (let i = 0; i < levelIndex; i++) {
      if (skillOrder[i] === '') {
        return `You must assign skills in order - fill in previous levels first`;
      }
    }
    
    if (ability.toLowerCase() === 'r') {
      // Check for skill position - ultimate can only be placed from level 6+
      if (level < 6) {
        return `Ultimate can only be learned at level 6 or higher`;
      }
      
      // Check champion level requirements
      if (currentLevel < 6) {
        return `Ultimate requires champion level 6+`;
      }
      
      // Check max points
      if (skillCounts.r >= 3) {
        return `Ultimate already at maximum rank (3 points)`;
      }
      
      // Check level requirements for additional points
      if (skillCounts.r >= 1 && currentLevel < 11) {
        return `Second point in ultimate requires champion level 11+`;
      }
      
      if (skillCounts.r >= 2 && currentLevel < 16) {
        return `Third point in ultimate requires champion level 16+`;
      }
    } else {
      // For basic abilities
      const key = ability.toLowerCase();
      const count = skillCounts[key as keyof typeof skillCounts];
      
      // Check max points for basic abilities - max 5 points
      if (count >= 5) {
        return `${ability} already at maximum rank (5 points)`;
      }
      
      // Check if we exceed the level/2 limit
      const maxPoints = Math.ceil(level / 2);
      if (count >= maxPoints) {
        return `At level ${level}, ${ability} can have max ${maxPoints} points`;
      }
    }
    
    return isValid ? `Level ${ability}` : `Cannot level ${ability} at level ${level}`;
  };
  
  // Render ability quick buttons for putting points in skills
  const renderAbilityButtons = () => {
    const abilities = ['Q', 'W', 'E', 'R'];
    
    return (
      <div className="skill-order-ability-buttons">
        {abilities.map((ability, index) => {
          const key = ability.toLowerCase();
          const count = skillCounts[key as keyof typeof skillCounts];
          const maxPoints = getAbilityMaxPoints(ability);
          const isMaxed = count >= maxPoints;
          const isUltimate = key === 'r';
          
          // For R button, disable if champion level < 6
          const isUltimateDisabled = isUltimate && currentLevel < 6;
          
          // Disable button if at max points or if no points remaining
          const isDisabled = !customMode || isMaxed || remainingPoints <= 0 || 
                            (isUltimate && isUltimateDisabled);
          
          return (
            <button
              key={ability}
              className={`skill-order-ability-button skill-order-ability-${key} ${isMaxed ? 'maxed' : ''}`}
              onClick={() => handleAbilityButtonClick(ability)}
              disabled={isDisabled}
              title={isMaxed ? `${ability} already at maximum rank (${maxPoints} points)` : 
                      isUltimateDisabled ? `Ultimate can only be learned at level 6+` :
                      `Quick add point to ${ability} (${count}/${maxPoints})`}
            >
              {renderSpellImage(index, ability)}
              <span className="skill-order-point-counter">{count}/{maxPoints}</span>
            </button>
          );
        })}
      </div>
    );
  };
  
  return (
    <div className="skill-order-control">
      <h4>Skill Order</h4>
      <div className="skill-order-container">
        <div className="skill-order-header-row">
          <div className="skill-order-points-info">
            <span>Ability Points: </span>
            <span className={remainingPoints > 0 ? 'skill-order-points-available' : ''}>
              {remainingPoints}/{currentLevel}
            </span>
          </div>
          
          <div className="skill-order-mode-toggles">
            <button 
              className={`skill-order-mode-button ${!customMode ? 'active' : ''}`}
              onClick={() => customMode && toggleCustomMode()}
            >
              Default
            </button>
            <button 
              className={`skill-order-mode-button ${customMode ? 'active' : ''}`}
              onClick={() => !customMode && toggleCustomMode()}
            >
              Custom
            </button>
          </div>
        </div>
        
        {/* Add the ability quick buttons - show them regardless of mode but disable in default mode */}
        {renderAbilityButtons()}
        
        {renderSkillGrid()}
        
        <div className="skill-order-actions">
          {customMode && (
            <button 
              className="skill-order-reset-button"
              onClick={resetCustomSkillOrder}
              title="Reset custom skill order to empty and redistribute all points"
            >
              Reset Points
            </button>
          )}
          <button 
            className="skill-order-apply-button"
            onClick={applySkillOrder}
            title="Apply skill order changes"
          >
            Apply Skill Order
          </button>
        </div>
      </div>
    </div>
  );
};

export default SkillOrderControl; 